import { Text } from "components/common";
import React, { forwardRef, useContext } from "react";
import { css } from "@emotion/css";
import { CARD_HEIGHT } from "utils/constants";
import { UserContext } from "context";
import { ShareBtn } from "./shareBtn";
import { SharableContent } from "./sharableContent";
import { Footer } from "./footer";

const offset = 50;
const isPersonal = (email: string) => {
  const isPersonalEmailRegex =
    /@(gmail|yahoo|hotmail|yahoo|aol|abc|xyz|pqr|rediffmail|live|outlook|me|msn|ymail)\.com$/;
  return isPersonalEmailRegex.test(email);
};

type Props = {
  handleNext?: VoidFunction;
  handleShare: (option: "twitter" | "facebook" | "linkedin") => void;
  index: number;
  ref: React.Ref<HTMLDivElement>;
  shareLoading?: boolean;
};
export const EmailRepartition = forwardRef<HTMLDivElement, Props>(
  ({ index, handleShare, shareLoading }, ref) => {
    const { state: userState } = useContext(UserContext);
    const data = userState?.emailStats?.emailPerCategory || {};

    const [isShare, setIsShare] = React.useState(false);
    const onShare = (option: "twitter" | "facebook" | "linkedin") => {
      setIsShare(true);
      setTimeout(async () => {
        await handleShare(option);
        setIsShare(false);
      }, 1000);
    };

    if (data["calendar"] || data["events"]) {
      data["calendar & event"] =
        (data["calendar"] || 0) + (data["events"] || 0);
      delete data["calendar"];
      delete data["events"];
    }

    const getTotalEmails = () => {
      return Object.keys(data).reduce((acc, key) => acc + data[key], 0);
    };

    const getLabel = (key: string) => {
      if (key === "@me") {
        return isPersonal(userState?.email || "")
          ? "work/personal"
          : "work/professional";
      }
      return key;
    };
    return (
      <>
        <div
          ref={ref}
          data-value={index}
          className={css`
            border: 1.825px solid #29282f;
            height: calc(100vh - ${offset}px);
            max-height: calc(${CARD_HEIGHT}px - ${offset}px);
            width: 100%;
            border-radius: 40px;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            align-items: center;
            padding: 50px 24px 65px 24px;
            transition: transform 0.3s;
            background-color: #151419;

            &:hover {
              transform: translateY(1px);
            }
            position: relative;
            overflow: auto;
          `}
        >
          <div>
            <Text.Body
              style={{
                marginBottom: "5px",
                fontSize: "24px",
                zIndex: 2,
                textAlign: "center",
              }}
            >
              You received
            </Text.Body>
            <Text.Body
              style={{
                marginBottom: "5px",
                marginTop: "5px",
                fontSize: "32px",
                zIndex: 2,
                textAlign: "center",
                color: "#AAEF65",
              }}
            >
              {getTotalEmails()} emails
            </Text.Body>
            <Text.Body
              style={{
                marginBottom: "20px",
                marginTop: "5px",
                fontSize: "24px",
                zIndex: 2,
                textAlign: "center",
              }}
            >
              that day
            </Text.Body>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              marginLeft: "-40px",
              width: "100%",
            }}
          >
            {Object.keys(data)
              .sort((a, b) => data[b] - data[a])
              .map((label, idx) => (
                <Item
                  key={label}
                  count={data[label]}
                  label={getLabel(label)}
                  index={idx}
                />
              ))}
          </div>

          {shareLoading ? (
            <Text.Body style={{ fontSize: "28px", zIndex: 2 }}>
              Sharing...
            </Text.Body>
          ) : (
            <ShareBtn handleShare={onShare} />
          )}
        </div>

        {isShare && (
          <SharableContent>
            <div
              ref={ref}
              data-value={index}
              className={css`
                width: 560px;
                height: 700px;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
                padding: 50px 24px 65px 24px;
                transition: transform 0.3s;
                background-color: #151419;
                position: relative;
                top: 0;
                left: 0;
                overflow: hidden;
              `}
            >
              <Text.Body
                style={{
                  fontSize: "30px",
                  zIndex: 2,
                  marginBottom: "50px",
                  textAlign: "center",
                }}
              >
                I received{" "}
                <span style={{ color: "#AAEF65" }}>{getTotalEmails()}</span>{" "}
                emails on my busiest day in the last 365 days.
              </Text.Body>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  marginLeft: "-40px",
                  width: "100%",
                  marginBottom: "40px",
                }}
              >
                {Object.keys(data)
                  .sort((a, b) => data[b] - data[a])
                  .map((label, idx) => (
                    <Item
                      key={label}
                      count={data[label]}
                      label={getLabel(label)}
                      index={idx}
                    />
                  ))}
              </div>

              <Text.Body
                style={{
                  fontSize: "30px",
                  zIndex: 2,
                  marginBottom: "8px",
                  textAlign: "center",
                }}
              >
                However, only{" "}
                <span style={{ color: "#AAEF65" }}>
                  {userState?.emailStats?.totalUrgentEmails || 0}
                </span>{" "}
                needed my attention that day.
              </Text.Body>

              <Text.Body
                style={{ fontSize: "18px", fontStyle: "italic", zIndex: 2 }}
                color="text/75%"
              >
                * based on Multiplayer's AI assessment
              </Text.Body>

              <Footer />
            </div>
          </SharableContent>
        )}
      </>
    );
  },
);

type ItemProps = {
  count: number;
  label: string;
  index: number;
};
function Item({ count, label, index }: ItemProps) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
        position: relative;
        align-items: center;
      `}
    >
      <div
        style={{
          marginLeft: -(index * 8) + "px",
          marginTop: -(index === 0 ? 0 : 14) + "px",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="186"
          height="60"
          viewBox="0 0 186 60"
          fill="none"
          style={{ height: 60 }}
        >
          <path
            d="M156 -1.14214e-06C172.569 -5.11352e-07 186 13.4315 186 30C186 46.5685 172.569 60 156 60L-3.01123e-06 60L0 -7.08125e-06L156 -1.14214e-06Z"
            fill="url(#paint0_linear_6234_4706)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_6234_4706"
              x1="167.748"
              y1="10.125"
              x2="74.6726"
              y2="90.7762"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#6A53FE" stop-opacity="0.56" />
              <stop offset="1" stop-color="#6A53FE" stop-opacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <Text.Body style={{ fontSize: "18px", marginLeft: "8px" }}>
        {" "}
        <span style={{ color: "#AAEF65" }}>{count}</span> {label}
      </Text.Body>
    </div>
  );
}
